import React, { useState, useEffect, useReducer } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setMessageHandler } from "../../../reducers/selectedValues";
import useHttpRequests from "../../../hooks/useHttpRequests";
import { Grid, CircularProgress, Backdrop } from "@mui/material";
import { InputDate } from "../../uiControls/inputDate";
import { InputTime } from "../../uiControls/inputTime";
import { AddNew } from "../../uiControls/addNew";
import { MessageDisplay } from "../../common/messageDisplay";
import { ReportsSelectCheckboxs } from "./reportsSelectCheckboxs";
import * as rptConst from "./externalReportsConstant";
import * as utils from "../../common/utils";
import moment from "moment";
import dayjs from "dayjs";

const initialReportIsValid = {
  startDate: "",
  endDate: "",
  startDateTime: "",
  endDateTime: "",
  startTime: "",
  endTime: "",
};

const initialSelectedValues = {
  reportId: "",
  report: [],
  reportUrl: "",
  taskClass: [],
  durationType: "",
  timeFrame: "",
  startDate: "",
  endDate: "",
  startDateTime: "",
  endDateTime: "",
  timeFormat: "",
  weekDays: [],
  startTime: "",
  endTime: "",
  startUnit: [],
  destinationUnit: [],
  taskStatus: [],
  routineStat: [],
  taskPriority: [],
  quarters: "",
};

export const ExternalReports = () => {
  const facilityId = useSelector((state) => state.SelectedValues.facilityId);
  const messageDispatch = useDispatch();
  const [reportList, setReportList] = useState([]);
  const [units, setUnits] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [taskClasses, setTaskClasses] = useState([]);
  const [lblDuration, setLblDuration] = useState("Specified");
  const [selectedRadio, setSelectedRadio] = useState("");
  const [functionalAreaKey, setFunctionalAreaKey] = useState("");
  const [selectedReportId, setSelectedReportId] = useState(null);

  const { isLoading: isLoadingReport, sendRequest: fetchReports } =
    useHttpRequests();
  const { isLoading: isLoadingReportDetails, sendRequest: fetchReportDetails } =
    useHttpRequests();

  const [reportSelectedValues, setReportSelectedValues] = useState(
    initialSelectedValues
  );

  const [isValid, setIsValid] = useReducer(
    (state, updates) => ({ ...state, ...updates }),
    initialReportIsValid
  );

  useEffect(() => {
    setTimeout(() => {
      updateReportSelectedValues(initialSelectedValues);
      setIsValid(initialReportIsValid);
      setReportList([]);
      setSelectedReportId(null);
      getReports();
    }, 0);
  }, [facilityId]);

  const resetSelectedValues = async () => {
    await updateReportSelectedValues(initialSelectedValues);
    setIsValid(initialReportIsValid);
    messageDispatch(setMessageHandler({ type: null, message: null }));
    getReportDetails(selectedReportId);
  };

  useEffect(() => {
    resetSelectedValues();
  }, [selectedReportId]);

  const getReports = () => {
    const getReportsResponse = (obj) => {
      if (!obj || !obj.isSuccess || obj.errorResponse) {
        messageDispatch(
          setMessageHandler({
            type: "error",
            message:
              (obj["message"] ? obj["message"] : "") +
              (obj["validationErrors"]?.length > 0
                ? "\n" + obj["validationErrors"]?.join("\n")
                : ""),
          })
        );
        return;
      }
      setFunctionalAreaKey(obj["functionalAreaKey"]);
      setUnits(
        obj["units"]?.map((t) => {
          return { id: t["key"], label: t["title"] };
        })
      );
      setPriorities(
        obj["priorities"]?.map((t) => {
          return { id: t["key"], label: t["title"] };
        })
      );
      setTaskClasses(
        obj["taskClasses"]?.map((t) => {
          return { id: t["key"], label: t["title"] };
        })
      );
      setReportList(
        obj["reports"]?.map((t) => {
          return { id: t["reportKey"], label: t["name"] };
        })
      );
    };

    if (facilityId > 0) {
      fetchReports(
        {
          url: "Facilities/" + facilityId + "/reports",
          method: "GET",
          header: true,
        },
        getReportsResponse
      );
    }
  };

  const getReportDetails = (id) => {
    const getReportDetailsResponse = (obj) => {
      if (!obj || !obj.isSuccess || obj.errorResponse) {
        messageDispatch(
          setMessageHandler({
            type: "error",
            message:
              (obj["message"] ? obj["message"] : "") +
              (obj["validationErrors"]?.length > 0
                ? "\n" + obj["validationErrors"]?.join("\n")
                : ""),
          })
        );
        return;
      }

      const rptDetails = utils.setStartEndDates(
        obj?.["startDate"],
        obj?.["endDate"]
      );
      rptDetails["reportId"] = obj["report"].reportKey;
      rptDetails["report"] = obj["report"];
      rptDetails["reportUrl"] = obj["reportUrl"];

      if (lblDuration !== "Specified") {
        rptDetails["startDate"] = moment.utc(obj["startDate"]).format("MM/DD/YYYY");
        rptDetails["endDate"] = moment.utc(obj["endDate"]).format("MM/DD/YYYY");
        rptDetails["startDateTime"] = " 12:00 AM";
        rptDetails["endDateTime"] = " 11:59 PM";
      }

      for (const [key, value] of Object.entries(rptDetails)) {
        updateReportSelectedValues(key, value);
      }
    };

    if (facilityId > 0 && id !== null) {
      fetchReportDetails(
        {
          url: "Facilities/" + facilityId + "/reports/" + id,
          method: "GET",
          header: true,
        },
        getReportDetailsResponse
      );
    }
  };

  const updateReportSelectedValues = (key, value) => {
    if (typeof key === "object") {
      setReportSelectedValues((prevState) => ({
        ...prevState,
        ...key,
      }));
    } else {
      setReportSelectedValues((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    }
  };

  const onDateChangeHandler = (e, controlName) => {
    updateReportSelectedValues(controlName, e);
  };

  const indexChangeHandler = (field, id, value) => {
    if (value) {
      if (id === "reportId") {
        setSelectedReportId(value);
      } else {
        updateReportSelectedValues(id, value);
      }
    }
  };

  const showFilters = (id) => {
    const isFilterExsits = reportSelectedValues?.report["filters"]?.filter(
      (f) => f.filterId === id
    );
    return isFilterExsits?.length > 0;
  };

  const validateInputs = () => {
    messageDispatch(setMessageHandler({ type: null, message: null }));
    let errMsg = "";

    if (!facilityId) {
      messageDispatch(
        setMessageHandler({ type: "error", message: "Select a facility..." })
      );
      return;
    }

    if (reportSelectedValues.reportId === "") {
      messageDispatch(
        setMessageHandler({ type: "error", message: "Select a report..." })
      );
      return;
    }

    if (functionalAreaKey === "") {
      errMsg += (errMsg ? "\n" : "") + "Functional area key is missing...";
    }

    if (reportSelectedValues.report[0]?.path !== "Task Specifics Report") {
      if (reportSelectedValues.startDate === "") {
        errMsg += (errMsg ? "\n" : "") + "Select start date...";
      }
      if (
        reportSelectedValues.endDate === "" &&
        reportSelectedValues.report[0]?.path !== "NPC_Performance_Dashboard" &&
        reportSelectedValues.report[0]?.path !== "NPC_Operations_Summary_Report"
      ) {
        errMsg += (errMsg ? "\n" : "") + "Select end date...";
      }
      if (
        reportSelectedValues.startDate !== "" &&
        reportSelectedValues.endDate !== "" &&
        (!moment.utc(reportSelectedValues.startDate).isValid() ||
          !moment.utc(reportSelectedValues.endDate).isValid() ||
          !moment.utc(
            !new Date(reportSelectedValues.startDateTime).getDate()
              ? "01/01/2023" + reportSelectedValues.startDateTime
              : reportSelectedValues.startDateTime
          ).isValid() ||
          !moment.utc(
            !new Date(reportSelectedValues.endDateTime).getDate()
              ? "01/01/2023" + reportSelectedValues.endDateTime
              : reportSelectedValues.endDateTime
          ).isValid())
      ) {
        errMsg += (errMsg ? "\n" : "") + "Select valid start/end date time...";
      }
    }

    if (
      (reportSelectedValues.startTime !== "" &&
        reportSelectedValues.endTime === "") ||
      (reportSelectedValues.startTime === "" &&
        reportSelectedValues.endTime !== "")
    ) {
      errMsg += (errMsg ? "\n" : "") + "Select start/end time...";
    }

    if (
      (reportSelectedValues.startTime !== "" &&
        !moment.utc(reportSelectedValues.startTime).isValid()) ||
      (reportSelectedValues.endTime !== "" &&
        !moment.utc(reportSelectedValues.endTime).isValid())
    ) {
      errMsg += (errMsg ? "\n" : "") + "Select valid start/end time...";
    }

    if (errMsg !== "") {
      messageDispatch(setMessageHandler({ type: "error", message: errMsg }));
      return;
    }

    // Helper to parse date string as UTC without timezone conversion
    // This ensures "01/15/2024" or "2024-01-15" stays as January 15th regardless of local timezone
    const parseDateAsUTC = (dateVal) => {
      if (!dateVal) return moment.utc();
      const dateStr = String(dateVal);
      // For ISO strings with time, parse directly
      if (dateStr.includes('T')) {
        return moment.utc(dateStr);
      }
      // Try multiple date formats - InputDate uses YYYY-MM-DD, some code uses MM/DD/YYYY
      let m = moment.utc(dateStr, ["YYYY-MM-DD", "MM/DD/YYYY"], true);
      if (!m.isValid()) {
        // Fallback: let moment try to parse
        m = moment.utc(dateStr);
      }
      return m;
    };

    // Helper to extract time (hours, minutes) from a time string, Date, or dayjs object
    const extractTime = (timeVal) => {
      if (!timeVal) return { hour: 0, minute: 0 };

      // Handle dayjs objects (from InputTime component)
      if (dayjs.isDayjs(timeVal)) {
        return { hour: timeVal.hour(), minute: timeVal.minute() };
      }

      // Handle Date objects
      if (timeVal instanceof Date) {
        return { hour: timeVal.getUTCHours(), minute: timeVal.getUTCMinutes() };
      }

      const timeStr = String(timeVal).trim();

      // If it's an ISO string, parse with moment UTC
      if (timeStr.includes('T')) {
        const m = moment.utc(timeVal);
        return { hour: m.hour(), minute: m.minute() };
      }

      // Parse time string like " 12:00 AM" or "11:59 PM"
      const timePart = timeStr.replace(/^\s+/, ''); // trim leading space
      const timeMatch = timePart.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
      if (timeMatch) {
        let hour = parseInt(timeMatch[1], 10);
        const minute = parseInt(timeMatch[2], 10);
        const period = timeMatch[3];
        if (period) {
          if (period.toUpperCase() === 'PM' && hour !== 12) hour += 12;
          if (period.toUpperCase() === 'AM' && hour === 12) hour = 0;
        }
        return { hour, minute };
      }
      return { hour: 0, minute: 0 };
    };

    // Convert startDateTime to UTC ISO string - combine startDate with time from startDateTime
    if (reportSelectedValues.startDateTime) {
      const startDateMoment = parseDateAsUTC(reportSelectedValues.startDate);
      const startTime = extractTime(reportSelectedValues.startDateTime);
      reportSelectedValues.startDateTime = startDateMoment
        .hour(startTime.hour)
        .minute(startTime.minute)
        .second(0)
        .toISOString();
    }

    // Convert endDateTime to UTC ISO string - combine endDate with time from endDateTime
    if (reportSelectedValues.endDateTime) {
      const endDateMoment = parseDateAsUTC(reportSelectedValues.endDate);
      const endTime = extractTime(reportSelectedValues.endDateTime);
      reportSelectedValues.endDateTime = endDateMoment
        .hour(endTime.hour)
        .minute(endTime.minute)
        .second(0)
        .toISOString();
    } else {
      // Default to end of day (23:59:59) if no time specified
      reportSelectedValues.endDateTime = parseDateAsUTC(reportSelectedValues.endDate)
        .hour(23)
        .minute(59)
        .second(59)
        .toISOString();
    }

    const reportParams = reportSelectedValues;
    reportParams["functionalAreaKey"] = functionalAreaKey;
    rptConst.buildReportUrl(reportParams, facilityId, {
      taskClasses: taskClasses,
      units: units,
      priorities: priorities,
    });
  };

  const getLabelDurationColor = (lblType) => {
    return lblDuration === lblType
      ? { display: "flex", color: "blue", fontWeight: "bold" }
      : { display: "flex", color: "inherit" };
  };

  const handleRadioChange = (event) => {
    messageDispatch(setMessageHandler({ type: null, message: null }));
    if (lblDuration === "") {
      messageDispatch(
        setMessageHandler({
          type: "error",
          message: "Select duration type i.e. Current/Previous/Specified.",
        })
      );
      return;
    } else if (lblDuration === "Specified") {
      setSelectedRadio("");
      return;
    }

    updateReportSelectedValues(
      utils.dateRangeFromSelectedOption(lblDuration, event.target.id)
    );
    setSelectedRadio(event.target.id);
  };

  const handleDurationTypeChange = (lblDuration) => {
    messageDispatch(setMessageHandler({ type: null, message: null }));
    setLblDuration(lblDuration);
    if (lblDuration !== "Specified") {
      updateReportSelectedValues(
        utils.dateRangeFromSelectedOption(lblDuration, selectedRadio)
      );
    } else {
      updateReportSelectedValues(
        utils.setStartEndDates(
          reportSelectedValues.startDate,
          reportSelectedValues.endDate
        )
      );
    }
  };

  return (
    <div
      style={{ display: "flex", gap: "20px", height: "calc(100vh - 160px)" }}
    >
      <Backdrop
        className="backDrop"
        open={isLoadingReport ? true : isLoadingReportDetails ? true : false}
      >
        <CircularProgress color="inherit" />
        <p>Please wait...</p>
      </Backdrop>
      <div
        style={{
          display: "flex",
          flexFlow: "column",
          width: "100%",
        }}
      >
        <MessageDisplay />
        {facilityId > 0 && (
          <Grid
            container
            sx={{ display: "flex", flexFlow: "nowrap", gap: "15px" }}
          >
            <Grid
              item
              sm={6}
              xs={12}
              sx={{ display: "flex", flexFlow: "column", gap: "15px" }}
            >
              <ReportsSelectCheckboxs
                isMulti={false}
                showInLeft={true}
                title="Reports"
                id="reportId"
                list={reportList}
                changeHandler={indexChangeHandler}
                startUnitDisplay={false}
                selectedReportId={selectedReportId}
              />
              {showFilters("divTaskClass") && (
                <ReportsSelectCheckboxs
                  isMulti={true}
                  showInLeft={true}
                  title="Task Classes"
                  id="taskClass"
                  list={taskClasses}
                  changeHandler={indexChangeHandler}
                  startUnitDisplay={false}
                  selectedReportId={selectedReportId}
                />
              )}
              <br />
              <br />

              {showFilters("divDurationType") && (
                <>
                  <Grid item sm={12} sx={{ display: "flex" }}>
                    <Grid
                      item
                      sm={2}
                      sx={getLabelDurationColor("Current")}
                      onClick={() => {
                        handleDurationTypeChange("Current");
                      }}
                    >
                      Current
                    </Grid>
                    <Grid
                      item
                      sm={2}
                      sx={getLabelDurationColor("Previous")}
                      onClick={() => {
                        handleDurationTypeChange("Previous");
                      }}
                    >
                      Previous
                    </Grid>
                    <Grid
                      item
                      sm={2}
                      sx={getLabelDurationColor("Specified")}
                      onClick={() => {
                        handleDurationTypeChange("Specified");
                      }}
                    >
                      Specified
                    </Grid>
                    <Grid item sm={6} sx={{ display: "flex" }}></Grid>
                  </Grid>
                  <Grid item sm={12} sx={{ padding: "0px" }}>
                    <Grid
                      item
                      sm={9}
                      sx={{
                        display: "flex",
                        border: "1px solid grey",
                        height: "1px",
                      }}
                    ></Grid>
                    <Grid item sm={3} sx={{ display: "flex" }}></Grid>
                  </Grid>
                  <Grid
                    id="divDateDuration"
                    item
                    sm={12}
                    sx={{ display: "flex", paddingBottom: "8px" }}
                  >
                    {rptConst.durations()?.map((rb, idx) => {
                      return (
                        <Grid key={idx} item sm={2} sx={{ display: "flex" }}>
                          <input
                            type="radio"
                            className="form-check-input"
                            id={rb.id}
                            name="dateDuration"
                            disabled={lblDuration === "Specified"}
                            checked={selectedRadio === rb.id}
                            onChange={handleRadioChange}
                          />
                          <label
                            className="form-check-label"
                            style={{ fontWeight: "normal" }}
                            htmlFor={rb.id}
                          >
                            &nbsp;
                            {rb.label}
                          </label>
                        </Grid>
                      );
                    })}
                    <Grid item sm={2} sx={{ display: "flex" }}></Grid>
                  </Grid>
                </>
              )}
              {showFilters("divTimeFrame") && (
                <ReportsSelectCheckboxs
                  isMulti={false}
                  showInLeft={true}
                  title="Time Frame"
                  id="timeFrame"
                  list={rptConst.timeFrameList()}
                  changeHandler={indexChangeHandler}
                  startUnitDisplay={false}
                  selectedReportId={selectedReportId}
                />
              )}
              {showFilters("divStartDate") && (
                <Grid item sm={12} sx={{ display: "flex" }}>
                  <Grid item sm={2} sx={{ display: "flex" }}>
                    Start Date:
                  </Grid>
                  <Grid item sm={6} sx={{ display: "flex" }}>
                    {lblDuration === "Specified" && (
                      <>
                        <InputDate
                          label=""
                          id={"reportSelectedValues.startDate"}
                          value={reportSelectedValues.startDate}
                          onChange={(e) => onDateChangeHandler(e, "startDate")}
                          isRequired={false}
                          isError={isValid["startDate"] !== ""}
                          displayType={"inline"}
                        />
                        <InputTime
                          label=""
                          id={"reportSelectedValues.startDateTime"}
                          selectedValue={reportSelectedValues.startDateTime}
                          onChange={(e) =>
                            onDateChangeHandler(e, "startDateTime")
                          }
                          isRequired={false}
                          isError={isValid["startDateTime"] !== ""}
                          hour12={true}
                          stepMinutes={15}
                        />
                      </>
                    )}
                    {lblDuration !== "Specified" &&
                      reportSelectedValues.startDate !== "" && (
                        <div style={{ height: "20px" }}>
                          {reportSelectedValues.startDate}
                          {reportSelectedValues.startDateTime}
                        </div>
                      )}
                  </Grid>
                  <Grid item sm={4} sx={{ display: "flex" }}></Grid>
                </Grid>
              )}
              {showFilters("divEndDate") && (
                <Grid
                  item
                  sm={12}
                  className="divEndDate hideFilters"
                  sx={{ display: "flex" }}
                >
                  <Grid item sm={2} sx={{ display: "flex" }}>
                    End Date:
                  </Grid>
                  <Grid item sm={6} sx={{ display: "flex" }}>
                    {lblDuration === "Specified" && (
                      <>
                        <InputDate
                          label=""
                          id={"reportSelectedValues.endDate"}
                          value={reportSelectedValues.endDate}
                          onChange={(e) => onDateChangeHandler(e, "endDate")}
                          isRequired={false}
                          isError={isValid["endDate"] !== ""}
                          displayType={"inline"}
                        />
                        <InputTime
                          label=""
                          id={"reportSelectedValues.endDateTime"}
                          selectedValue={reportSelectedValues.endDateTime}
                          onChange={(e) =>
                            onDateChangeHandler(e, "endDateTime")
                          }
                          isRequired={false}
                          isError={isValid["endDateTime"] !== ""}
                          hour12={true}
                          stepMinutes={15}
                        />
                      </>
                    )}
                    {lblDuration !== "Specified" &&
                      reportSelectedValues.endDate !== "" && (
                        <div style={{ height: "20px" }}>
                          {reportSelectedValues.endDate}
                          {reportSelectedValues.endDateTime}
                        </div>
                      )}
                  </Grid>
                  <Grid item sm={4} sx={{ display: "flex" }}></Grid>
                </Grid>
              )}
              {showFilters("divTimeFormat") && (
                <ReportsSelectCheckboxs
                  isMulti={false}
                  showInLeft={true}
                  title="Time Format"
                  id="timeFormat"
                  list={rptConst.timeFormatList()}
                  changeHandler={indexChangeHandler}
                  startUnitDisplay={false}
                  selectedReportId={selectedReportId}
                />
              )}
              {showFilters("divWeekDays") && (
                <ReportsSelectCheckboxs
                  isMulti={true}
                  showInLeft={true}
                  title="Week Days"
                  id="weekDays"
                  list={rptConst.weekDayList()}
                  changeHandler={indexChangeHandler}
                  startUnitDisplay={false}
                  selectedReportId={selectedReportId}
                />
              )}
              {(showFilters("divStartTime") ||
                showFilters("divStartTimeHour")) && (
                <Grid item sm={12} sx={{ display: "flex" }}>
                  <Grid item sm={2} sx={{ display: "flex" }}>
                    Start Time:
                  </Grid>
                  <Grid item sm={3} sx={{ display: "flex" }}>
                    <InputTime
                      label=""
                      id={"reportSelectedValues.startTime"}
                      selectedValue={reportSelectedValues.startTime}
                      onChange={(e) => onDateChangeHandler(e, "startTime")}
                      isRequired={false}
                      isError={isValid["startTime"] !== ""}
                      hour12={true}
                      stepMinutes={rptConst.getMinuteSteps(
                        reportSelectedValues.report
                      )}
                    />
                  </Grid>
                  <Grid item sm={7} sx={{ display: "flex" }}></Grid>
                </Grid>
              )}
              {(showFilters("divEndTime") || showFilters("divEndTimeHour")) && (
                <Grid item sm={12} sx={{ display: "flex" }}>
                  <Grid item sm={2} sx={{ display: "flex" }}>
                    End Time:
                  </Grid>
                  <Grid item sm={3} sx={{ display: "flex" }}>
                    <InputTime
                      label=""
                      id={"reportSelectedValues.endTime"}
                      selectedValue={reportSelectedValues.endTime}
                      onChange={(e) => onDateChangeHandler(e, "endTime")}
                      isRequired={false}
                      isError={isValid["endTime"] !== ""}
                      hour12={true}
                      stepMinutes={rptConst.getMinuteSteps(
                        reportSelectedValues.report
                      )}
                    />
                  </Grid>
                  <Grid item sm={7} sx={{ display: "flex" }}></Grid>
                </Grid>
              )}

              <Grid item sm={12} sx={{ display: "flex" }}>
                <Grid item sm={4} sx={{ display: "flex" }}></Grid>
                <Grid item sm={2} sx={{ display: "flex" }}>
                  <AddNew onClickCall={validateInputs} title={"Load"} />
                </Grid>
                <Grid item sm={6} sx={{ display: "flex" }}></Grid>
              </Grid>
            </Grid>

            <Grid
              sm={6}
              xs={12}
              item
              sx={{
                display: "flex",
                flexFlow: "column",
                height: "100px",
                gap: "15px",
              }}
            >
              {showFilters("divGeography") && (
                <Grid item sm={12} sx={{ display: "flex" }}>
                  <Grid item sm={2}></Grid>
                  <Grid item sm={4}>
                    {showFilters("divHeaderDestination") && <>Destination:</>}
                  </Grid>
                  <Grid item sm></Grid>
                  <Grid item sm={4}>
                    {showFilters("divHeaderStart") && <>Start:</>}
                  </Grid>
                  <Grid item sm></Grid>
                </Grid>
              )}
              {showFilters("divHeaderUnit") &&
                showFilters("divDestinationUnit") && (
                  <ReportsSelectCheckboxs
                    isMulti={true}
                    showInLeft={false}
                    title="Unit"
                    id="destinationUnit"
                    list={units}
                    changeHandler={indexChangeHandler}
                    startUnitDisplay={showFilters("divStartUnit")}
                    startUnitDisabled={
                      rptConst.toggleUnits(reportSelectedValues)?.startUnit
                    }
                    destUnitDisabled={
                      rptConst.toggleUnits(reportSelectedValues)?.destUnit
                    }
                    selectedReportId={selectedReportId}
                  />
                )}
              {showFilters("divTaskStatus") && (
                <ReportsSelectCheckboxs
                  isMulti={true}
                  showInLeft={false}
                  title="Task Status"
                  id="taskStatus"
                  list={rptConst.taskStatusList()}
                  changeHandler={indexChangeHandler}
                  startUnitDisplay={false}
                  selectedReportId={selectedReportId}
                />
              )}
              {showFilters("divRoutineStat") && (
                <ReportsSelectCheckboxs
                  isMulti={true}
                  showInLeft={false}
                  title="Task Type"
                  id="routineStat"
                  list={rptConst.taskTypeList()}
                  changeHandler={indexChangeHandler}
                  startUnitDisplay={false}
                  selectedReportId={selectedReportId}
                />
              )}
              {showFilters("divQuarters") && (
                <ReportsSelectCheckboxs
                  isMulti={false}
                  showInLeft={false}
                  title="Quarters"
                  id="quarters"
                  list={rptConst.quartersList()}
                  changeHandler={indexChangeHandler}
                  startUnitDisplay={false}
                  selectedReportId={selectedReportId}
                />
              )}
              {showFilters("divPriority") && (
                <ReportsSelectCheckboxs
                  isMulti={true}
                  showInLeft={false}
                  title="Task Priority"
                  id="taskPriority"
                  list={priorities}
                  changeHandler={indexChangeHandler}
                  startUnitDisplay={false}
                  selectedReportId={selectedReportId}
                />
              )}
            </Grid>
          </Grid>
        )}
      </div>
    </div>
  );
};
